import { forwardRef } from 'react'
import { ButtonProps } from './props.js'
import { Spinner } from './Spinner.js'

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      size = 'md',
      icon,
      loading = false,
      fullWidth = false,
      className = '',
      children,
      disabled,
      ...attrs
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    // Base classes common to all variants
    const baseClasses = [
      'relative inline-flex items-center justify-center gap-2',
      'rounded-full transition-colors interactive',
      'focus-visible:outline-2 focus-visible:outline-offset-2',
      'disabled:opacity-40 disabled:cursor-not-allowed',
      fullWidth && 'w-full',
    ]
      .filter(Boolean)
      .join(' ')

    // Size-based dimensions
    const sizeClasses: Record<string, string> = {
      sm: 'h-8 px-4 text-label-md',
      md: 'h-9 px-4 text-label-lg',
      lg: 'h-11 px-6 text-label-lg',
    }

    // Variant-specific classes
    let variantClasses: string[] = []

    if (variant === 'filled') {
      variantClasses = [
        'bg-primary text-on-primary hover:opacity-90',
      ]
    } else if (variant === 'tonal') {
      variantClasses = [
        'bg-surface-highest text-on-surface hover:bg-surface-high',
      ]
    } else if (variant === 'outlined') {
      variantClasses = [
        'border border-outline bg-transparent text-on-surface hover:bg-surface-high',
      ]
    } else if (variant === 'text') {
      variantClasses = [
        'text-on-surface-variant hover:text-on-surface px-2',
      ]
    } else if (variant === 'danger') {
      variantClasses = [
        'bg-error-container text-on-error-container border border-error-container hover:bg-error-container',
      ]
    }

    const finalClassName = [
      baseClasses,
      sizeClasses[size],
      variantClasses.join(' '),
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        className={finalClassName}
        {...attrs}
      >
        {loading && icon ? <Spinner size={size} /> : icon}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
